/*
  Glooty "Pick your 3" builder (sections/glooty-b2g1-builder.liquid, the page /pages/buy-2-get-1;
  docs/offer/IMPLEMENT-B2G1-SITEWIDE.md, P4). She picks 3 pieces, the cheapest one is free.

  Every number is whole-bag math (docs/offer/FIX-1.7.1.md, A): Shopify's code works on the whole bag
  (every unit price sorted, the floor(n / 3) cheapest are free), so the picks are priced together
  with the pieces already in the bag. data-bag: the bag's lines the offer counts, [variant id, unit
  price, quantity] in the bag's order (pieces of the offer collection, data-offer = its product ids;
  empty = every product with a price), data-rest: the total of its other lines; both from Liquid's
  cart at render, then read again from /cart.js on every bag change (Dawn's
  PUB_SUB_EVENTS.cartUpdate, the event 'glooty:b2g1:cart', back/forward cache, the tab coming back)
  and with the builder's own add. Empty bag: the numbers are the 3 picks alone, as they always were.
  Equal prices are settled the way Shopify settles them (measured on the store, 1.7.1): the unit of
  a line with fewer pieces first, then the newest line (the top of the bag), so the slot that
  reads FREE is the line the bag will show FREE.

  1. Cards: each card carries its variants as JSON (data-g-data). Picking a colour pebble swaps the
     photo and the colour name; pills that cannot be bought with the other picks are struck through;
     the price follows the pick. With 2 pieces picked, each card says what it adds as the 3rd one
     ("FREE as your 3rd" when the bag total would not move, else "+$X as your 3rd"); with an empty
     bag that is the bag's order bump math (free when not dearer than the cheapest picked).
  2. "Add to my 3" fills the next slot (size first: the size pills get a nudge and a message). A
     second tap within 700 ms on the same card is ignored, so a double tap adds one piece. A slot
     reads FREE only when its pick is one of the free units of the bag it would make; with 3
     picked, those move to the end (the cheapest in the last slot). With pieces in the bag the
     empty last slot reads 3, not FREE: the free one may be hers. The picks live in sessionStorage,
     so a trip to the size guide does not lose them.
  3. The bar: n/3, the full price struck and the price to pay (sum minus the cheapest), "You save $X"
     and "Ships free" only when the new bag total reaches the free-shipping threshold (data-ship,
     cents; empty = never). With pieces in the bag: what the picks add to it ("+$100", "You save
     $38 more") and "Your bag's 3 pieces count too."; when a cheaper pick takes the free spot of a
     piece already in the bag (a FREE slot next to "+$54"), that line says so instead: "Your bag's
     free $54 piece goes back to full price." The live region says the same numbers. The
     button scrolls to the grid until 3 are picked, then adds them all: POST /cart/add.js with the
     drawer sections, then the offer code goes on the bag (data-code, POST /cart/update.js, expired
     codes of earlier offers come off, as in assets/glooty-b2g1.js) and the drawer opens with the
     sections of the last request, so it reads "Free piece unlocked". No drawer: /cart. Busy while
     a request runs and a second tap within a second is ignored, so it can't add twice.
  4. Other scripts hear about the add: Dawn's PUB_SUB_EVENTS.cartUpdate (source 'cart-items', so
     Dawn's drawer does not redraw a second time; glootyB2G1 and glootyBuilder are true) and a
     'glooty:b2g1-builder:added' event on document ({ detail: { items, cart } }).
  Needs Dawn's global.js (fetchConfig), pubsub.js / constants.js and cart-drawer.js (renderContents).
*/
if (!customElements.get('glooty-b2g1-builder')) {
  const MAX = 3;
  // Codes of earlier offers, expired: taken off the bag (same list as assets/glooty-b2g1.js)
  const EXPIRED_CODES = ['WINGWOMAN', 'SECONDCOLOR'];
  const STORE_KEY = 'glooty-b2g1-picks';
  const X_ICON =
    '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>';

  const same = (a, b) =>
    String(a || '')
      .trim()
      .toUpperCase() ===
    String(b || '')
      .trim()
      .toUpperCase();

  const route = (name, fallback) => (window.routes && window.routes[name]) || fallback;

  const postJson = (url, body) => {
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig('json')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    config.body = JSON.stringify(body);
    return fetch(url, config).then((response) => response.json());
  };

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class GlootyB2G1Builder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.picks = [];
      this.uid = 0;
      this.done = false;
      this.busy = false;
      this.money = this.moneyFormatter();
      this.cards = [...this.querySelectorAll('[data-g-card]')];
      this.slots = [...this.querySelectorAll('[data-g-slot]')];
      this.live = this.querySelector('[data-g-live]');
      if (!this.cards.length) return;

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);

      this.readData();
      this.watchBag();
      this.cards.forEach((card) => this.refresh(card));
      this.restore();
      this.render();
      this.watchHeader();
    }

    disconnectedCallback() {
      if (this.onChange) this.removeEventListener('change', this.onChange);
      if (this.onClick) this.removeEventListener('click', this.onClick);
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      if (this.onBagEvent) document.removeEventListener('glooty:b2g1:cart', this.onBagEvent);
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
      window.clearTimeout(this.bagTimer);
      this.ready = false;
    }

    // "$108" / "$45.50" in the bag's currency, like money_without_trailing_zeros
    moneyFormatter() {
      const currency = this.dataset.currency || 'USD';
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const make = (digits) => {
        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: digits,
            maximumFractionDigits: 2,
          });
        } catch (error) {
          return null;
        }
      };
      const whole = make(0);
      const cents = make(2);
      return (amount) => {
        const value = amount / 100;
        const format = Number.isInteger(value) ? whole : cents;
        return format ? format.format(value) : String(value);
      };
    }

    // The sticky slots sit under Dawn's sticky header while it shows (it slides away on the way down)
    watchHeader() {
      const header = document.querySelector('.section-header');
      if (!header || typeof MutationObserver === 'undefined') return;
      const update = () => {
        const shown =
          header.classList.contains('shopify-section-header-sticky') &&
          !header.classList.contains('shopify-section-header-hidden');
        this.classList.toggle('is-header-shown', shown);
      };
      update();
      this.headerObserver = new MutationObserver(update);
      this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
    }

    // ---------- The bag ----------

    // The bag as Liquid saw it: the lines the offer counts (data-bag: [variant id, unit price,
    // quantity], newest first, as cart.items lists them; a bare number is one piece of an unknown
    // line) and the total of the other lines (data-rest); data-offer = product ids of the offer
    // collection
    readData() {
      const parse = (value) => {
        try {
          const list = JSON.parse(value || '[]');
          return Array.isArray(list) ? list : [];
        } catch (error) {
          return [];
        }
      };
      const offer = parse(this.dataset.offer).map(String);
      this.offer = offer.length ? new Set(offer) : null;
      const lines = parse(this.dataset.bag)
        .map((entry) =>
          Array.isArray(entry)
            ? { id: Number(entry[0]) || null, p: Number(entry[1]) || 0, q: Number(entry[2]) || 0 }
            : { id: null, p: Number(entry) || 0, q: 1 }
        )
        .filter((line) => line.p > 0 && line.q > 0);
      this.bag = this.makeBag(lines, Number(this.dataset.rest) || 0);
    }

    // { lines: [{ id, p, q }] newest first, units: every piece's price (sorted), rest }
    makeBag(lines, rest) {
      const units = [];
      lines.forEach((line) => {
        for (let unit = 0; unit < line.q; unit += 1) units.push(line.p);
      });
      return { lines, units: units.sort((a, b) => a - b), rest };
    }

    // A line the offer counts: a price, not a gift card, in the offer collection (when it is known)
    counts(item) {
      return item.original_price > 0 && !item.gift_card && (!this.offer || this.offer.has(String(item.product_id)));
    }

    watchBag() {
      this.onBagEvent = (event) => this.readBag(event.detail);
      this.onPageShow = (event) => {
        if (event.persisted) this.readBag();
      };
      this.onVisible = () => {
        if (document.visibilityState === 'visible') this.readBag();
      };
      document.addEventListener('glooty:b2g1:cart', this.onBagEvent);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('visibilitychange', this.onVisible);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // The builder's own add (glootyBuilder) already read the bag
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || !event.glootyBuilder) this.readBag(event && event.cartData);
        });
      }
    }

    // A whole bag (/cart.js, /cart/change.js, /cart/update.js: item_count + items) replaces the one
    // the builder knows; anything else (an /cart/add.js answer) is not one. Repaints unless an add
    // is running (its end repaints).
    setBag(cart) {
      if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) return false;
      const lines = [];
      let rest = 0;
      cart.items.forEach((item) => {
        if (!this.counts(item)) {
          rest += Number(item.final_line_price) || 0;
          return;
        }
        if (item.quantity > 0) lines.push({ id: Number(item.variant_id) || null, p: item.original_price, q: item.quantity });
      });
      this.bag = this.makeBag(lines, rest);
      if (!this.busy) this.render();
      return true;
    }

    // The bag changed: the announcement when it is a whole bag, else /cart.js (several announcements
    // in a row make one request)
    readBag(cart) {
      window.clearTimeout(this.bagTimer);
      this.bagTimer = window.setTimeout(() => {
        if (this.setBag(cart)) return;
        fetch(`${route('cart_url', '/cart')}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((fresh) => this.setBag(fresh))
          .catch(() => null);
      }, 60);
    }

    // What the picks do to the bag, the way Shopify's code works it out: every unit of the bag and of
    // the picks sorted by price, the floor(n / 3) cheapest are free. Equal prices go the way Shopify
    // takes them (measured on the store, 1.7.1): first a unit of a line with fewer pieces (a lone
    // Black L before a pair of Black M), then the newest line (the top of the bag). The picks go in
    // as the add sends them: one line per variant, in the order first picked, landing on top of the
    // bag with the last one first; a variant already in the bag joins its line (the bag's own pieces
    // of that line count first).
    // { sum: the picks at full price, add: what the bag total goes up by, save: how much more the bag
    //   saves (with an empty bag: the cheapest of 3), total: the new bag total, free: uids of the
    //   picks that come out free, lost: { count, price }: the bag's free pieces that go back to full
    //   price (net of bag pieces that become free; price when it is one piece) }
    outcome(list) {
      const lines = this.bag.lines;
      const total = (prices) => prices.reduce((sum, price) => sum + price, 0);
      const order = (a, b) => a.p - b.p || a.q - b.q || a.pos - b.pos || a.side - b.side;

      // The add, as addAll sends it; a pick of a variant already in the bag joins that line
      const items = [];
      list.forEach((pick) => {
        const item = pick.id != null ? items.find((entry) => entry.id === pick.id) : null;
        if (item) item.picks.push(pick);
        else items.push({ id: pick.id, picks: [pick] });
      });
      const joins = new Map();
      const fresh = [];
      items.forEach((item) => {
        const at = item.id != null ? lines.findIndex((line) => line.id === item.id) : -1;
        if (at >= 0 && !joins.has(at)) joins.set(at, item);
        else fresh.push(item);
      });

      // The bag's own units, and which of them are free now (same order, the bag alone)
      const bagUnits = [];
      lines.forEach((line, index) => {
        for (let unit = 0; unit < line.q; unit += 1) bagUnits.push({ p: line.p, q: line.q, pos: index, side: 0, line: index });
      });
      bagUnits
        .slice()
        .sort(order)
        .slice(0, Math.floor(bagUnits.length / 3))
        .forEach((unit) => (unit.was = true));

      // After the add: the new lines on top (the last one first), then the bag's lines
      const units = [];
      fresh.forEach((item, index) => {
        const pos = fresh.length - 1 - index;
        item.picks.forEach((pick) => units.push({ p: pick.p, q: item.picks.length, pos, side: 1, pick }));
      });
      bagUnits.forEach((unit) => {
        const join = joins.get(unit.line);
        unit.q = lines[unit.line].q + (join ? join.picks.length : 0);
        unit.pos = fresh.length + unit.line;
        units.push(unit);
      });
      joins.forEach((item, at) => {
        const q = lines[at].q + item.picks.length;
        item.picks.forEach((pick) => units.push({ p: pick.p, q, pos: fresh.length + at, side: 1, pick }));
      });
      units.sort(order);
      const freeUnits = units.slice(0, Math.floor(units.length / 3));
      freeUnits.forEach((unit) => (unit.now = true));

      const sum = total(list.map((pick) => pick.p));
      const before = total(bagUnits.filter((unit) => unit.was).map((unit) => unit.p));
      const save = total(freeUnits.map((unit) => unit.p)) - before;
      const lost = bagUnits.filter((unit) => unit.was && !unit.now);
      const gained = bagUnits.filter((unit) => !unit.was && unit.now);
      const moved = Math.max(0, lost.length - gained.length);
      return {
        sum,
        save,
        add: sum - save,
        total: total(this.bag.units) - before + sum - save + this.bag.rest,
        free: new Set(freeUnits.filter((unit) => unit.pick).map((unit) => unit.pick.uid)),
        lost: { count: moved, price: moved === 1 && lost.length === 1 ? lost[0].p : 0 },
      };
    }

    // "Your bag's 3 pieces count too."; when a pick takes the free spot of a piece already in the bag
    // (the slot reads FREE while the bar adds more than the paid picks), it says why instead
    bagLine(result) {
      const lost = result && result.lost;
      if (lost && lost.count === 1) {
        return lost.price
          ? `Your bag’s free ${this.money(lost.price)} piece goes back to full price.`
          : 'Your bag’s free piece goes back to full price.';
      }
      if (lost && lost.count > 1) return `Your bag’s ${lost.count} free pieces go back to full price.`;
      const count = this.bag.units.length;
      return count === 1 ? 'Your bag’s 1 piece counts too.' : `Your bag’s ${count} pieces count too.`;
    }

    // ---------- Cards ----------

    data(card) {
      if (!card.gData) {
        const node = card.querySelector('[data-g-data]');
        try {
          card.gData = JSON.parse(node ? node.textContent : '{}');
        } catch (error) {
          card.gData = {};
        }
        card.gData.ch = card.gData.ch || [];
        card.gData.v = card.gData.v || [];
      }
      return card.gData;
    }

    keys(card) {
      if (!card.gKeys) {
        card.gKeys = [...new Set([...card.querySelectorAll('input[data-g-opt]')].map((input) => input.dataset.gOpt))];
      }
      return card.gKeys;
    }

    // Picks so far: colour index and option values by name (missing = not picked yet)
    state(card) {
      const color = card.querySelector('input[data-g-color]:checked');
      const options = {};
      card.querySelectorAll('input[data-g-opt]:checked').forEach((input) => {
        options[input.dataset.gOpt] = input.value;
      });
      return { c: color ? Number(color.value) : 0, o: options };
    }

    // Variants that fit the picks (a missing pick fits anything)
    matches(card, state) {
      const keys = this.keys(card);
      return this.data(card).v.filter(
        (variant) => variant.c === state.c && keys.every((key) => state.o[key] == null || variant.o[key] === state.o[key])
      );
    }

    exact(card, state) {
      const keys = this.keys(card);
      if (keys.some((key) => state.o[key] == null)) return null;
      return this.matches(card, state)[0] || null;
    }

    // Photo, colour name, struck-through pills and the price, for the picks so far
    refresh(card) {
      const data = this.data(card);
      let state = this.state(card);
      const choice = data.ch[state.c] || data.ch[0];

      const colorName = card.querySelector('[data-g-color-name]');
      if (colorName && choice) colorName.textContent = choice.n;

      const img = card.querySelector('.g-b2g1b__img');
      if (img && choice && choice.img && Number(img.dataset.gChoice || 0) !== state.c) {
        img.dataset.gChoice = String(state.c);
        if (choice.set) img.srcset = choice.set;
        img.src = choice.img;
        img.alt = choice.n ? `${data.t}, ${choice.n}` : data.t || '';
      }

      // A pill whose variant (with the other picks) cannot be bought is struck through and disabled
      card.querySelectorAll('.g-b2g1b__pill input').forEach((input) => {
        const test = { c: state.c, o: { ...state.o, [input.dataset.gOpt]: input.value } };
        const available = this.matches(card, test).some((variant) => variant.a);
        input.disabled = !available;
        if (!available && input.checked) input.checked = false;
      });

      state = this.state(card);
      const prices = this.matches(card, state).map((variant) => variant.p);
      card.gPrice = prices.length ? Math.min(...prices) : null;
      const price = card.querySelector('[data-g-price]');
      if (price && card.gPrice != null) price.textContent = this.money(card.gPrice);
      this.hint(card);
    }

    // With 2 picked: what this card adds to the bag as the 3rd piece (whole-bag math; with an empty
    // bag the cheapest of the three is free, so FREE when it is not dearer than the cheapest picked)
    hint(card) {
      const hint = card.querySelector('[data-g-hint]');
      if (!hint) return;
      if (this.picks.length !== MAX - 1 || card.gPrice == null) {
        hint.hidden = true;
        return;
      }
      const delta = this.outcome(this.picks.concat({ p: card.gPrice })).add - this.outcome(this.picks).add;
      hint.textContent = delta > 0 ? `+${this.money(delta)} as your 3rd` : 'FREE as your 3rd';
      hint.classList.toggle('is-plus', delta > 0);
      hint.hidden = false;
    }

    onChange(event) {
      const card = event.target.closest('[data-g-card]');
      if (!card) return;
      const field = event.target.closest('.g-b2g1b__field');
      if (field) field.classList.remove('is-alert');
      this.setMessage(card, '');
      this.refresh(card);
    }

    onClick(event) {
      const add = event.target.closest('[data-g-add]');
      if (add) {
        this.pick(add.closest('[data-g-card]'), add);
        return;
      }
      const remove = event.target.closest('[data-g-remove]');
      if (remove) {
        this.remove(Number(remove.dataset.gRemove));
        return;
      }
      const cta = event.target.closest('[data-g-cta]');
      if (cta) {
        if (this.picks.length >= MAX) this.addAll(cta);
        else this.toGrid();
      }
    }

    // ---------- Slots ----------

    pick(card, button) {
      if (!card || this.busy) return;
      const now = Date.now();
      if (card.gLast && now - card.gLast < 700) return;

      const state = this.state(card);
      const missing = this.keys(card).find((key) => state.o[key] == null);
      if (missing) {
        const field = [...card.querySelectorAll('.g-b2g1b__field[data-g-key]')].find(
          (item) => item.dataset.gKey === missing
        );
        if (field) {
          field.classList.remove('is-alert');
          void field.offsetWidth;
          field.classList.add('is-alert');
          const first = field.querySelector('input:not(:disabled)');
          if (first) first.focus();
        }
        this.setMessage(card, `Pick your ${(field && field.dataset.gLabel) || missing} first.`);
        return;
      }

      if (this.picks.length >= MAX) {
        this.setMessage(card, 'Your 3 are picked. Remove one to swap it.');
        return;
      }

      const variant = this.exact(card, state);
      if (!variant || !variant.a) {
        this.setMessage(card, 'That one can’t be picked. Try another size or color.');
        return;
      }

      card.gLast = now;
      const pick = this.makePick(card, variant);
      this.picks.push(pick);
      this.done = false;
      this.save();
      this.render(pick.uid);
      this.setMessage(card, '');
      this.flash(button);

      const count = this.picks.length;
      let line = `${pick.name}, ${pick.vals} added. ${count} of 3 picked.`;
      if (count === MAX) {
        const result = this.outcome(this.picks);
        if (!this.bag.units.length) {
          line += ` You pay ${this.money(result.add)}, you save ${this.money(result.save)}.`;
        } else {
          const more = result.save > 0 ? `, you save ${this.money(result.save)} more` : '';
          line += ` Adds ${this.money(result.add)} to your bag${more}. ${this.bagLine(result)}`;
        }
      }
      this.say(line);
    }

    makePick(card, variant) {
      const data = this.data(card);
      const choice = data.ch[variant.c] || {};
      const parts = [];
      if (card.querySelector('input[data-g-color]') && choice.n) parts.push(choice.n);
      card.querySelectorAll('.g-b2g1b__field[data-g-key]').forEach((field) => {
        const value = variant.o[field.dataset.gKey];
        if (value) parts.push(value);
      });
      this.uid += 1;
      return { uid: this.uid, id: variant.id, p: variant.p, name: data.t || '', vals: parts.join(' · '), th: choice.th || '' };
    }

    remove(uid) {
      const index = this.picks.findIndex((pick) => pick.uid === uid);
      if (index < 0 || this.busy) return;
      const [pick] = this.picks.splice(index, 1);
      this.done = false;
      this.save();
      this.render();
      this.say(`${pick.name}, ${pick.vals} removed. ${this.picks.length} of 3 picked.`);
      const next = this.querySelector('[data-g-remove]') || this.querySelector('[data-g-slots]');
      if (next) next.focus();
    }

    // Slot order: as picked; with 3, the picks that come out free go last (the cheapest in the last
    // slot, the FREE one)
    ordered(free) {
      const list = this.picks.slice();
      if (list.length !== MAX) return list;
      const paid = list.filter((pick) => !free.has(pick.uid));
      const gratis = list.filter((pick) => free.has(pick.uid)).sort((a, b) => b.p - a.p);
      return paid.concat(gratis);
    }

    render(newUid) {
      const result = this.outcome(this.picks);
      const list = this.ordered(result.free);
      const count = list.length;
      const bagged = this.bag.units.length > 0;

      this.slots.forEach((slot, index) => {
        const pick = list[index];
        // A pick that comes out free; empty, the last slot reads FREE only while the bag is empty
        const isFree = pick ? result.free.has(pick.uid) : index === MAX - 1 && !bagged;
        const label = isFree ? 'Free piece' : `Piece ${index + 1}`;
        slot.className = [
          'g-b2g1b__slot',
          isFree ? 'g-b2g1b__slot--free' : '',
          pick ? 'is-filled' : '',
          pick && pick.uid === newUid ? 'is-new' : '',
        ]
          .filter(Boolean)
          .join(' ');
        if (!pick) {
          slot.innerHTML = `<span class="g-b2g1b__slot-num" aria-hidden="true">${isFree ? 'FREE' : index + 1}</span><span class="visually-hidden">${label}: empty</span>`;
          return;
        }
        const tag = isFree ? 'FREE' : this.money(pick.p);
        const img = pick.th
          ? `<img class="g-b2g1b__slot-img" src="${escapeHtml(pick.th)}" alt="" width="160" height="200" loading="lazy">`
          : '<span class="g-b2g1b__slot-img"></span>';
        slot.innerHTML = `${img}<span class="g-b2g1b__slot-tag${isFree ? ' g-b2g1b__slot-tag--free' : ''}">${tag}</span><span class="g-b2g1b__slot-name"><span class="visually-hidden">${label}: </span>${escapeHtml(pick.name)}</span><span class="g-b2g1b__slot-vals">${escapeHtml(pick.vals)}</span><button type="button" class="g-b2g1b__slot-x" data-g-remove="${pick.uid}" aria-label="Remove ${escapeHtml(pick.name)}, ${escapeHtml(pick.vals)}">${X_ICON}</button>`;
      });

      this.paintBar(count, result);
      this.dataset.count = String(count);
      this.cards.forEach((card) => this.hint(card));
    }

    // Empty bag: "$162 struck · $108 · You save $54 · Ships free". Pieces in the bag: what the picks
    // add to it, "$138 struck · +$100 · You save $38 more · Ships free" and the bag's line (the
    // element gets is-bagged, for the CSS)
    paintBar(count, result) {
      const bagged = this.bag.units.length > 0;
      const ship = Number(this.dataset.ship || 0);
      const ships = count === MAX && ship > 0 && result.total >= ship;
      const off = result.add < result.sum;
      const set = (selector, text, show = true) => {
        const node = this.querySelector(selector);
        if (!node) return;
        if (text != null) node.textContent = text;
        node.hidden = !show;
      };

      this.querySelectorAll('.g-b2g1b__dots i').forEach((dot, index) => dot.classList.toggle('is-on', index < count));
      set('[data-g-count]', `${count}/3`);
      set('[data-g-full]', this.money(result.sum), off);
      set('[data-g-full-label]', null, off);
      set('[data-g-pay]', `${bagged ? '+' : ''}${this.money(result.add)}`, count > 0);
      set('[data-g-pay-label]', bagged ? 'Adds to your bag' : 'You pay', count > 0 && (off || bagged));

      let save = 'Your cheapest piece is free.';
      if (this.done && count === 0) save = 'Added to your bag. Want another 3?';
      else if (bagged) {
        const parts = [];
        if (count > 0 && result.save > 0) parts.push(`You save ${this.money(result.save)} more`);
        if (ships) parts.push('Ships\u00a0free'); // kept together if the line wraps
        save = parts.join(' · ');
      } else if (count === MAX) save = `You save ${this.money(result.save)}${ships ? ' · Ships\u00a0free' : ''}`;
      set('[data-g-save]', save, Boolean(save));
      set('[data-g-bag]', bagged ? this.bagLine(result) : '', bagged);
      this.classList.toggle('is-bagged', bagged);

      const cta = this.querySelector('[data-g-cta]');
      if (cta) {
        cta.classList.toggle('is-ready', count === MAX);
        const label = cta.querySelector('[data-g-cta-label]');
        if (label) label.textContent = count === MAX ? 'Add all 3 to bag' : `Pick ${MAX - count} more`;
      }
      this.setBarMessage('');
    }

    // The grid's top lands just under the sticky slots (and under Dawn's header when it will show:
    // it slides back in when the page scrolls up, and always shows with the "always" sticky types)
    toGrid() {
      const grid = this.querySelector('[data-g-grid]');
      if (!grid) return;
      const top = grid.getBoundingClientRect().top;
      const wrap = this.querySelector('.g-b2g1b__slots-wrap');
      const header = document.querySelector('.section-header');
      const sticky = document.querySelector('sticky-header');
      const always = Boolean(sticky && ['always', 'reduce-logo-size'].includes(sticky.dataset.stickyType));
      const headerHeight = header && sticky && (always || top < 0) ? header.getBoundingClientRect().height : 0;
      const offset = (wrap ? wrap.offsetHeight : 0) + headerHeight + 12;
      window.scrollTo({ top: Math.max(0, window.scrollY + top - offset), behavior: reducedMotion() ? 'auto' : 'smooth' });
      const title = this.querySelector('[data-g-grid-title]');
      if (title) title.focus({ preventScroll: true });
    }

    // ---------- Add all 3 ----------

    addAll(button) {
      // Busy while the requests run, and a second tap within a second never adds again (even when
      // the first request already failed fast)
      const now = Date.now();
      if (this.busy || this.picks.length < MAX || now - (this.lastAdd || 0) < 1000) return;
      this.lastAdd = now;
      this.busy = true;
      const spinner = button.querySelector('.loading__spinner');
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      if (spinner) spinner.classList.remove('hidden');
      this.setBarMessage('');

      // Two of the same variant go in as one line of 2
      const items = [];
      this.picks.forEach((pick) => {
        const line = items.find((item) => item.id === pick.id);
        if (line) line.quantity += 1;
        else items.push({ id: pick.id, quantity: 1 });
      });

      const drawer = document.querySelector('cart-drawer');
      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : [];
      const body = { items };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      const linesUpdate = this.createLinesUpdateEvent(items);

      postJson(`${route('cart_add_url', '/cart/add')}.js`, body)
        .then((response) => {
          if (response.status) {
            const error = new Error(response.description || response.message || '');
            error.fromShop = true;
            throw error;
          }
          this.resolveLinesUpdate(linesUpdate);
          return this.applyCode(sections).then((updated) => ({
            response,
            cart: updated,
            html: (updated && updated.sections) || response.sections,
          }));
        })
        .then(({ response, cart, html }) => {
          this.picks = [];
          this.done = true;
          this.save();
          this.render();
          this.announceCart(response, items, cart);
          this.say('Added to your bag. Want another 3?');
          if (drawer && html && html['cart-drawer'] && typeof drawer.renderContents === 'function') {
            drawer.classList.remove('is-empty');
            if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(button);
            drawer.renderContents({ id: items[0].id, sections: html });
          } else {
            window.location.href = route('cart_url', '/cart');
          }
        })
        .catch((error) => {
          if (linesUpdate) linesUpdate.reject(error);
          this.setBarMessage(
            error && error.fromShop && error.message ? `That didn’t go in: ${error.message}` : 'That didn’t go in. Try again?'
          );
        })
        .finally(() => {
          this.busy = false;
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    // Offer code on the bag once it has 2+ pieces, expired codes off. Resolves with the update
    // response (it carries the drawer sections) or null when nothing had to change or it failed.
    // The bag it reads (and the update's answer) become the builder's bag, for the next 3.
    applyCode(sections) {
      const code = (this.dataset.code || '').trim();
      return fetch(`${route('cart_url', '/cart')}.js`)
        .then((response) => response.json())
        .then((cart) => {
          this.setBag(cart);
          const codes = (cart.discount_codes || []).map((entry) => entry.code);
          const pieces = (cart.items || []).reduce(
            (total, item) => total + (item.original_price > 0 ? item.quantity : 0),
            0
          );
          const want = codes.filter((value) => !EXPIRED_CODES.some((expired) => same(expired, value)));
          if (code && pieces >= 2 && !want.some((value) => same(value, code))) want.push(code);
          const changed = want.length !== codes.length || want.some((value, index) => !same(value, codes[index]));
          if (!changed) return null;
          const body = { discount: want.join(',') };
          if (sections.length) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
          }
          return postJson(`${route('cart_update_url', '/cart/update')}.js`, body).then((updated) => {
            this.setBag(updated);
            return updated;
          });
        })
        .catch(() => {
          this.readBag();
          return null;
        });
    }

    announceCart(response, items, cart) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: cart || response,
          glootyB2G1: true,
          glootyBuilder: true,
        });
      }
      document.dispatchEvent(new CustomEvent('glooty:b2g1-builder:added', { detail: { items, cart: cart || null } }));
    }

    // Shopify's standard cart events (analytics), as Dawn's product form sends them
    createLinesUpdateEvent(items) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      // a failed add is already shown on the bar; this keeps it from also surfacing as an unhandled rejection
      if (deferred.promise && typeof deferred.promise.catch === 'function') deferred.promise.catch(() => null);
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'page',
          lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
      return deferred;
    }

    resolveLinesUpdate(deferred) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!deferred || !CartLinesUpdateEvent) return;
      fetch(`${route('cart_url', '/cart')}.json`)
        .then((response) => response.json())
        .then((cart) => {
          if (!cart || !cart.currency) throw new Error('Missing currency in cart response');
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        })
        .catch((error) => deferred.reject(error));
    }

    // ---------- Small helpers ----------

    save() {
      try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(this.picks.map((pick) => pick.id)));
      } catch (error) {
        /* storage blocked: the picks just don't survive a reload */
      }
    }

    restore() {
      let ids = [];
      try {
        ids = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '[]');
      } catch (error) {
        ids = [];
      }
      if (!Array.isArray(ids)) return;
      ids.slice(0, MAX).forEach((id) => {
        for (const card of this.cards) {
          const variant = this.data(card).v.find((item) => item.id === Number(id));
          if (variant && variant.a) {
            this.picks.push(this.makePick(card, variant));
            break;
          }
        }
      });
    }

    flash(button) {
      const label = button && button.querySelector('[data-g-add-label]');
      if (!label) return;
      window.clearTimeout(button.gFlash);
      button.classList.add('is-added');
      label.textContent = 'Added';
      button.gFlash = window.setTimeout(() => {
        button.classList.remove('is-added');
        label.textContent = 'Add to my 3';
      }, 1200);
    }

    setMessage(card, text) {
      const box = card && card.querySelector('[data-g-msg]');
      if (!box) return;
      box.textContent = '';
      // a fresh text node, so screen readers announce the same message twice in a row
      if (text) window.setTimeout(() => (box.textContent = text), 30);
    }

    setBarMessage(text) {
      const box = this.querySelector('[data-g-bar-msg]');
      if (box) box.textContent = text;
    }

    say(text) {
      if (!this.live) return;
      this.live.textContent = '';
      window.setTimeout(() => (this.live.textContent = text), 30);
    }
  }

  customElements.define('glooty-b2g1-builder', GlootyB2G1Builder);
}
